import React from "react";
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Link } from "@mui/material";
import RawSearchInfoButton from "../../../../Component/RawSearchInfoButton/RawSearchInfoButton";
import SentimentFace from "./SentimentFace";

const headerCellSx = {
  color: "#fff",
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  border: "none",
  py: "9px",
};

function SourceLinks({ sources }) {
  if (!sources || sources.length === 0) {
    return <Typography sx={{ fontSize: 12, color: "#c3c9d3" }}>No sources</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
      {sources.slice(0, 3).map((url, i) => (
        <Link
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: 11.5, color: "#1976d2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}
        >
          {url.replace(/^https?:\/\//, "")}
        </Link>
      ))}
      {sources.length > 3 && (
        <Typography sx={{ fontSize: 11, color: "#667085" }}>+{sources.length - 3} more</Typography>
      )}
    </Box>
  );
}

/** Brand item table for a topic. */
function ItemTable({ items }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Table sx={{ width: "100%" }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#4fd1c5" }}>
            <TableCell sx={{ ...headerCellSx, borderRadius: "6px 0 0 6px", width: "24%" }}>Item</TableCell>
            <TableCell sx={{ ...headerCellSx, width: "38%" }}>Explanation</TableCell>
            <TableCell sx={{ ...headerCellSx, width: "24%" }}>Sources</TableCell>
            <TableCell sx={{ ...headerCellSx, borderRadius: "0 6px 6px 0", width: "14%", textAlign: "center" }}>Sentiment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i} sx={{ "&:last-of-type td": { borderBottom: "none" } }}>
              <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#1a2027", verticalAlign: "top" }}>{item.topic}</TableCell>
              <TableCell sx={{ fontSize: 12.5, color: "#475467", lineHeight: 1.4, verticalAlign: "top" }}>
                {item.explanation ? `"${item.explanation}"` : "—"}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <SourceLinks sources={item.sources} />
              </TableCell>
              <TableCell sx={{ textAlign: "center", verticalAlign: "top" }}>
                <SentimentFace score={item.sentiment_score} explanation={item.sentiment_score_explanation} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * One topic's brand item table. Category-side data is never read or
 * rendered here — brand-only throughout.
 *
 * `bare`: used when nested inline inside an already-expanded Topic Bucket
 * Breakdown row — skips the outer card/shadow and the topic title (the row
 * above already shows the name), keeping just the info icon and table.
 */
export default function TopicItemTable({ topicLabel, entityData, bare = false }) {
  if (!entityData?.items?.length) return null;

  const rawSearch = entityData?.rawSearch || null;
  const table = <ItemTable items={entityData.items} />;

  if (bare) {
    return (
      <Box>
        {rawSearch && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <RawSearchInfoButton data={rawSearch} label={topicLabel} />
          </Box>
        )}
        {table}
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0px 3.5px 5.5px 0px rgba(0,0,0,0.05)", p: "20px 24px" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1a2027" }}>{topicLabel}</Typography>
        {rawSearch && <RawSearchInfoButton data={rawSearch} label={topicLabel} />}
      </Box>
      {table}
    </Box>
  );
}
